<template>
  <div>
    <template v-if="loaded && !loadError">
      <div class="wrapper">
        <template v-if="!isComplaintStarted">
          <div class="my-2 width-medium mx-auto">

            <f-alert
              theme="warning"
              visible
              small>
              {{ $t('inquiryComplaints.complaintProcess') }}
            </f-alert>

            <f-button
              class="mt-4 centered-box-relative "
              theme="danger"
              @click.prevent="issueComplaint">
              {{ $t('inquiryComplaints.makeComplaint') }}
            </f-button>
          </div>
        </template>
        <template v-else>
          <f-alert
            v-if="data.is_archived && loaded"
            class="my-2 width-medium mx-auto"
            theme="warning"
            visible
            small>
            {{ $t('message.topic.archiveTopicSuccessTitle') }}
          </f-alert>

          <div class="card is-smallshadow mt-2 mb-5 width-medium mx-auto">
            <div class="card-body px-3 pt-2">
              <div class="border-bottom pb-1 flex">
                <h3 class="heading-medium flex flex-align-items-center flex-justify-content-between full-width message-header">
                  {{ $t('inquiryComplaints.yourComplaint') }}
                </h3>

                <f-button
                  v-if="!data.is_archived && loaded"
                  class="ml-auto no-shrink"
                  theme="text"
                  @click.prevent="archiveTopic">
                  <f-icon name="archive" class="mr-0-xs" />
                  <span class="hidden-mobile-tiny">{{ $t('ui.archive') }}</span>
                </f-button>
              </div>
              <f-message-history
                class="mt-3"
                :items="messageFormatted"/>

              <template v-if="!data.is_archived">
                <validation-observer
                  ref="complaint-form"
                  tag="form"
                  class="border-top pt-4 mt-4">
                  <div class="form-group mb-0">
                    <label for="message">{{ $t('messageForm.writeTheMessage') }}: </label>

                    <validation-provider
                      rules="required|max:2000"
                      name="message"
                      v-slot="{ errors }">
                      <f-textarea
                        v-model="newMessage"
                        :maxLength="200"
                        :showCounter="true"
                        :rows="6"
                        :state="errors.length ? 'invalid' : null"/>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </validation-observer>

                <div class="flex mt-2 ">
                  <div>
                    <f-file-input
                      id="complaintAttachment"
                      label="Dodaj załącznik"
                      icon="attachment"
                      accept=".pdf, .jpg, .jpeg, .png, .gif, .doc, .docx, .xls, .xlsx"
                      :multiple="true"
                      :onChange="attachFiles"/>
                  </div>

                  <f-button
                    class="ml-auto"
                    :disabled="!newMessage || !newMessage.length || actionInProgress"
                    theme="primary"
                    withArrow
                    big
                    @click.prevent="validateForm">
                    {{ $t('ui.send') }}
                  </f-button>
                </div>

                <div
                  v-if="files.length"
                  class="flex flex-column mt-2 attach-files-list full-width">
                  <span class="heading-tiny mb-2">{{ $t('messageForm.attachedFiles') }}:</span>
                  <template
                    v-for="(file, index) of files">
                    <span
                      :key="index"
                      class="attach-files-list-item">
                      <div class="mr-3 mb-2">
                        <label>{{ $t('messageForm.fileName') }}</label>
                        {{ file.name }}
                      </div>
                      <label>{{ $t('messageForm.descriptionFile') }}</label>
                      <f-textarea
                        fullWidth
                        v-model="descriptions[index]"
                        :rows="2"/>

                      <f-button
                        class="ml-auto mt-2 block"
                        theme="danger"
                        :disabled="actionInProgress"
                        @click.prevent="removeFile(index)">
                        {{ $t('messageForm.deleteFile') }}
                      </f-button>
                    </span>
                  </template>
                </div>

              </template>
            </div>
          </div>
        </template>

      </div>
    </template>
    <template v-else>
      <div
        v-if="!loaded && !loadError"
        class="loading-spinner-wrapper">
        <div class="loading-spinner pb-3 pt-3">
          <span class="loading-spinner-content">
            {{ $t('ui.loadingDataInProgress') }}&hellip;
          </span>
        </div>
      </div>
      <div class="wrapper mt-3">
        <f-alert
          v-if="loadError"
          theme="warning"
          visible>
          <span>
            {{ $t('ui.dataLoadError.dataErrorMess') }} <a href="javascript:window.location.reload();" class="lnk lnk-alert">{{ $t('ui.dataLoadError.resetPageButton') }}</a> {{ $t('ui.dataLoadError.forTryAgain') }}
          </span>
        </f-alert>
      </div>
    </template>
  </div>
</template>

<script>
import Vue from 'vue';
import FormUtils from '@/utils/FormUtils.js';

export default {
  name: 'inquiry-camplaints',
  props: {
    inquiryID: {
      type: Number,
      default: 0
    },
    inquiryComplaintsTopicID: {
      type: Number,
      default: 0
    }
  },
  computed: {
    messageFormatted () {
      return this.data.messages.map(item => {
        item.author = 'Administrator';
        if (item.user_id === this.data.customer_id) {
          item.isFromCurrentUser = true;
          item.author = this.$t('messageForm.myMessage');
        }

        item.downloadFileEndpoint = '/api/customer/complaints/download-file/';

        return item;
      });
    }
  },
  data() {
    return {
      data: {
        id: 0,
        inquiry_id: 0,
        adviser_id: 0,
        customer_id: 0,
        customer_name: '',
        customer_surname: '',
        customer_email: '',
        customer_phone: '',
        customer_post_code: '',
        customer_city: '',
        is_archived: false,
        messages: []
      },
      loaded: false,
      loadError: false,
      newMessage: '',
      files: [],
      descriptions: [],
      isComplaintStarted: false,
      actionInProgress: false,
      shouldMarkAsRead: false
    };
  },
  mounted () {
    this.$bus.$on('mark-complaint-as-read', this.markAsRead);
    if (this.inquiryID) {
      this.data.inquiry_id = this.inquiryID;
    }
    if (this.inquiryComplaintsTopicID) {
      this.isComplaintStarted = true;
      this.loadItemData(this.inquiryComplaintsTopicID);
    } else {
      this.loaded = true;
    }
  },
  methods: {
    issueComplaint () {
      Vue.set(this, 'isComplaintStarted', true);
    },
    loadItemData (topicID) {
      FormUtils.loadItemData(this, {
        id: topicID,
        method: 'get',
        endpointBase: '/api/customer/complaints/topics/item/',
        instanceFields: [
          'id',
          'inquiry_id',
          'adviser_id',
          'customer_id',
          'customer_name',
          'customer_surname',
          'customer_email',
          'customer_phone',
          'customer_post_code',
          'customer_city',
          'is_archived',
          'messages'
        ],
        loadedKey: 'loaded',
        successAction: () => {
          Vue.nextTick(() => {
            if (this.shouldMarkAsRead) {
              Vue.set(this, 'shouldMarkAsRead', false);
              this.markAsRead();
            }
          })
        }
      });
    },
    submitForm () {
      this.actionInProgress = true;
      let formDataToSend = this.prepareFormDataToSend();
      let endpointUrl = '/api/customer/complaints/store';

      let fileParams = [];
      if (formDataToSend.files && formDataToSend.files.length) {
        fileParams.push('files');
      }

      FormUtils.submit(this, {
        endpoint: endpointUrl,
        formData: formDataToSend,
        successTitle: this.$t('inquiryComplaints.sendComplaint'),
        successText: this.$t('inquiryComplaints.yourComplaintHasBeenSend'),
        errorTitle: this.$t('ui.errorOccurred'),
        errorText: this.$t('inquiryComplaints.sendComplaintError'),
        successAction: () => {
          this.actionInProgress = false;
          this.clearForm();
          Vue.nextTick(() => {
            let reloadComplaints = !this.inquiryComplaintsTopicID;
            this.$bus.$emit('inquiry-reload', 'inquiry-complaints', reloadComplaints);
          })
        },
        failAction: () => {
          this.actionInProgress = false;
        }
      }, fileParams);
    },
    prepareFormDataToSend () {
      let formDataToSend = {
        message: this.newMessage,
      };

      if (this.data.inquiry_id) {
        formDataToSend.inquiry_id = this.data.inquiry_id;
      }

      if (this.data.id) {
        formDataToSend.topic_id = this.data.id;
      }

      if (this.files.length) {
        formDataToSend.files = [];

        for (let i = 0; i < this.files.length; i++) {
          formDataToSend.files.push(this.files[i]);
        }
      }

      if (this.descriptions) {
        formDataToSend.descriptions = [];

        for (let i = 0; i < this.descriptions.length; i++) {
          if (this.descriptions[i]) {
            formDataToSend.descriptions.push(this.descriptions[i]);
          } else {
            formDataToSend.descriptions.push('');
          }
        }
      }

      return formDataToSend;
    },
    validateForm () {
      FormUtils.validate(this.$refs['complaint-form'], this.submitForm);
    },
    clearForm() {
      this.newMessage = '';
      this.files = [];
      this.descriptions = [];
      this.$refs['complaint-form'].reset();
    },
    attachFiles (event) {
      this.files.push(event.target.files[0]);
    },
    removeFile (index) {
      this.files.splice(index, 1);

      if (this.descriptions[index]) {
        this.descriptions.splice(index, 1);
      }
    },
    markAsRead () {
      if (!this.data.id) {
        Vue.set(this, 'shouldMarkAsRead', true);
        return;
      }

      this.$http.post('/api/customer/complaints/topics/mark-as-read/' + this.data.id, {
      }).then(() => {
        this.$bus.$emit('reset-complaint-messages-count');
      }).catch((error) => {
        console.log(error);
      });
    },
    archiveTopic () {
      FormUtils.submitWithConfirm(this, {
        endpoint: '/api/customer/complaints/topics/archive/' + this.data.id,
        successTitle: this.$t('inquiryComplaints.complaintArchived'),
        successText: this.$t('inquiryComplaints.complaintHasBeenArchived'),
        confirmTitle: this.$t('inquiryComplaints.areYouSureYouWantToArchiveClaim'),
        confirmText: this.$t('ui.thisOperationCannotUndone'),
        confirmButtonText: this.$t('message.topic.archiveTopicConfirmBtnTxt'),
        errorTitle: this.$t('ui.errorOccurred'),
        errorText: this.$t('inquiryComplaints.complaintArchivedError'),
        successAction: () => {
          this.loadItemData(this.data.id);
        }
      });
    },
  },
  beforeDestroy () {
    this.$bus.$off('mark-complaint-as-read', this.markAsRead);
  }
}
</script>
